import { MailDirection, ProcessingStatus } from "../EmailSearchAPI";
import { DateRangePreset, EmailFilters, forDirection } from "../searchCriteria";

interface iFilterBarProps {
    filters: EmailFilters;
    /** The tenant's mailboxes, for the mailbox filter's options. */
    mailboxes: { mailboxID: number; name?: string }[];
    /** The STS roster, for the tenant picker. */
    tenants: { key: string; name: string }[];
    /** The tenant currently open. Page state, not a filter — see `onTenantChange`. */
    tenantKey: string;
    onTenantChange: (tenantKey: string) => void;
    onChange: (filters: EmailFilters) => void;
}

const DIRECTIONS: MailDirection[] = ["Inbound", "Outbound"];

/**
 * The statuses offered per direction.
 *
 * A `Record`, so a third direction cannot compile without a decision.
 * `Abandoned` is a send-give-up state and appears only outbound; `Processing`
 * is inbound's in-flight state and `Building`/`Sendable` are outbound's.
 */
const STATUS_OPTIONS: Record<MailDirection, ProcessingStatus[]> = {
    Inbound: ["Failed", "DeadLettered", "Queued", "Processing", "Completed"],
    Outbound: ["Failed", "DeadLettered", "Abandoned", "Queued", "Building", "Sendable", "Sent"],
};

/**
 * The two non-status values the status select can hold.
 *
 * `failedOnly` and `statuses` AND together on the server (spec §3.2), so a
 * request may carry one or the other and never both. Two independent controls
 * made that a rule the component had to police — disable the chips, explain
 * why, clear the stale selection when the switch came back on. One select
 * makes the illegal combination unrepresentable instead, which is why all of
 * that machinery is gone.
 */
const FAILED = "failed";
const ANY_STATUS = "any";

/**
 * The last-activity windows, in the order they are offered.
 *
 * A `Record` over the whole `DateRangePreset` union, so a new preset cannot
 * compile until it has a label. Iterated with `Object.keys`, whose order for
 * non-numeric string keys is insertion order per the language spec — so this
 * literal's order is the order on screen.
 */
const DATE_RANGES: Record<DateRangePreset, string> = {
    any: "Any time",
    "3h": "Last 3 hours",
    "6h": "Last 6 hours",
    "12h": "Last 12 hours",
    "24h": "Last 24 hours",
    "7d": "Last 7 days",
    "30d": "Last 30 days",
    custom: "Custom range",
};

function FilterBar({ filters, mailboxes, tenants, tenantKey, onTenantChange, onChange }: iFilterBarProps) {
    /*
     * `statuses` wins over the failure switch, matching `toCriteria`, which
     * prefers a non-empty `statuses` and drops `failedOnly` when it has one.
     * If this read them the other way round the select would display a filter
     * the server was not being asked for.
     */
    const statusValue = filters.statuses?.[0] ?? (filters.failedOnly ? FAILED : ANY_STATUS);

    function chooseStatus(value: string) {
        if (value === FAILED) {
            onChange({ ...filters, failedOnly: true, statuses: undefined });
            return;
        }
        if (value === ANY_STATUS) {
            onChange({ ...filters, failedOnly: false, statuses: undefined });
            return;
        }
        // The failure switch goes off with every explicit status, so the pair
        // can never reach `toCriteria` in the combination it has to arbitrate.
        onChange({ ...filters, failedOnly: false, statuses: [value as ProcessingStatus] });
    }

    return (
        <div className="mp-filters">
            <div className="mp-filter-row">
                {/*
                    A MODE SWITCH, not a filter. `direction` is required by the
                    API and selects the row shape, so there is no "both" and no
                    way to clear it. Exactly one of Inbound/Outbound is ever
                    active — never both, never neither — which is radio
                    semantics, not independently-toggleable buttons. Native
                    `<input type="radio">` gives correct AT announcement and
                    keyboard behaviour (arrow-key navigation, single tab stop)
                    for free; a hand-rolled `role="radio"` would need a roving
                    tabindex to match that. Visually styled as a segmented
                    control by `mp-seg`/`mp-seg-on`/`mp-seg-off` — the legend is
                    kept for the accessible name and is meant to be visually
                    hidden by `mp-seg-legend`, not removed.
                */}
                <fieldset className="mp-seg">
                    <legend className="mp-seg-legend">Direction</legend>
                    {DIRECTIONS.map(direction => (
                        <label
                            key={direction}
                            className={filters.direction === direction ? "mp-seg-on" : "mp-seg-off"}
                        >
                            <input
                                type="radio"
                                className="mp-seg-input"
                                name="mp-direction"
                                value={direction}
                                checked={filters.direction === direction}
                                onChange={() => onChange(forDirection(filters, direction))}
                            />
                            {direction}
                        </label>
                    ))}
                </fieldset>

                <label className="mp-field">
                    Status
                    <select
                        aria-label="Status"
                        value={statusValue}
                        onChange={event => chooseStatus(event.target.value)}
                    >
                        {/*
                            The server owns what "failed" means — both
                            repositories derive it from the pipeline's own
                            terminal set (spec §3.2) — so this asks for
                            `failedOnly` rather than naming the statuses here
                            and drifting the day that set changes.
                        */}
                        <option value={FAILED}>All failed statuses</option>
                        <option value={ANY_STATUS}>Any status</option>
                        {STATUS_OPTIONS[filters.direction].map(status => (
                            <option
                                key={status}
                                value={status}
                            >
                                {status}
                            </option>
                        ))}
                    </select>
                </label>

                {/*
                    Pushed right by `mp-filter-right`'s auto margin. These two
                    pick WHAT IS BEING LOOKED AT rather than narrowing it, so
                    they sit apart from the filters on the left.
                */}
                <div className="mp-filter-right">
                    <label className="mp-field">
                        Tenant
                        <select
                            aria-label="Tenant"
                            className="mp-tenant-picker"
                            value={tenantKey}
                            onChange={event => onTenantChange(event.target.value)}
                        >
                            {tenants.map(tenant => (
                                <option
                                    key={tenant.key}
                                    value={tenant.key}
                                >
                                    {tenant.name}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="mp-field">
                        Mailbox
                        <select
                            aria-label="Mailbox"
                            value={filters.mailboxID ?? ""}
                            onChange={event =>
                                onChange({
                                    ...filters,
                                    mailboxID: event.target.value === "" ? undefined : Number(event.target.value),
                                })
                            }
                        >
                            <option value="">All mailboxes</option>
                            {mailboxes.map(mailbox => (
                                <option
                                    key={mailbox.mailboxID}
                                    value={mailbox.mailboxID}
                                >
                                    {mailbox.name ?? `Mailbox ${mailbox.mailboxID}`}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            </div>

            <div className="mp-filter-row">
                {/*
                    "Last activity", NOT "Received". Both directions filter
                    `UpdatedAt`, which surfaces as `processingUpdatedAt`;
                    neither filters receivedAt or sentAt. A "received between"
                    label returns plausible rows and is silently wrong.
                */}
                <label className="mp-field">
                    Last activity
                    <select
                        aria-label="Last activity"
                        value={filters.dateRange}
                        onChange={event => onChange({ ...filters, dateRange: event.target.value as DateRangePreset })}
                    >
                        {(Object.keys(DATE_RANGES) as DateRangePreset[]).map(preset => (
                            <option
                                key={preset}
                                value={preset}
                            >
                                {DATE_RANGES[preset]}
                            </option>
                        ))}
                    </select>
                </label>

                {/*
                    Only rendered for a custom range. The two inputs keep their
                    values while a preset is selected — `toCriteria` ignores
                    them unless the preset is `custom` — so switching back to
                    Custom restores the range that was last typed rather than
                    clearing it.
                */}
                {filters.dateRange === "custom" && (
                    <>
                        <label className="mp-field">
                            From
                            <input
                                type="date"
                                aria-label="Last activity from"
                                value={filters.dateFrom?.slice(0, 10) ?? ""}
                                onChange={event =>
                                    onChange({
                                        ...filters,
                                        dateFrom: event.target.value ? `${event.target.value}T00:00:00` : undefined,
                                    })
                                }
                            />
                        </label>

                        <label className="mp-field">
                            To
                            <input
                                type="date"
                                aria-label="Last activity to"
                                value={filters.dateTo?.slice(0, 10) ?? ""}
                                onChange={event =>
                                    onChange({
                                        ...filters,
                                        dateTo: event.target.value ? `${event.target.value}T23:59:59` : undefined,
                                    })
                                }
                            />
                        </label>
                    </>
                )}

                {/* Matches subject, sender and recipient only — never the body. */}
                <input
                    type="search"
                    className="mp-search"
                    aria-label="Search text"
                    placeholder="Subject, from or to"
                    value={filters.text ?? ""}
                    onChange={event => onChange({ ...filters, text: event.target.value })}
                />
            </div>
        </div>
    );
}

export default FilterBar;
